import { fCurrency } from '@/utils/formatNumber';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

const ProductTools = ({ product }) => (
  <Box display="flex" flexWrap="wrap" gap={0.5}>
    {product.affiliate_share_bump && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <img src="/assets/icons/showcase/cash.svg" alt="Order bump" width={18} height={18} />
        <Typography
          color="text.secondary"
          sx={{
            fontSize: 12,
          }}
        >
          Order bump
        </Typography>
      </Box>
    )}
    {product.affiliate_share_upsell && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <img src="/assets/icons/showcase/arrow-up.svg" alt="Upsell" width={16} height={16} />
        <Typography
          color="text.secondary"
          sx={{
            fontSize: 12,
          }}
        >
          Upsell
        </Typography>
      </Box>
    )}
    {!!product.affiliate_sales_page && (
      <Link to={product.affiliate_sales_page} target="_blank">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <img
            src="/assets/icons/showcase/user.svg"
            alt="Affiliate sales page"
            width={16}
            height={16}
          />
          <Typography
            color="text.secondary"
            sx={{
              fontSize: 12,
            }}
          >
            Página do afiliado
          </Typography>
        </Box>
      </Link>
    )}
    {product.affiliate_clone_quiz && (
      <Link to={product.affiliate_clone_quiz_url} target="_blank">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <img src="/assets/icons/showcase/copy.svg" alt="Copy quiz" width={16} height={16} />
          <Typography
            color="text.secondary"
            sx={{
              fontSize: 12,
            }}
          >
            Duplicar quiz
          </Typography>
        </Box>
      </Link>
    )}
    {product.type && product.type === 'subscription' && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <img
          src="/assets/icons/showcase/subscription.svg"
          alt="Recorrente"
          width={16}
          height={16}
        />
        <Typography
          color="text.secondary"
          sx={{
            fontSize: 12,
          }}
        >
          Recorrente
        </Typography>
      </Box>
    )}
  </Box>
);

ProductTools.propTypes = {
  product: PropTypes.shape({
    affiliate_share_bump: PropTypes.bool,
    affiliate_share_upsell: PropTypes.bool,
    sales_page: PropTypes.string,
    affiliate_sales_page: PropTypes.string,
    affiliate_clone_quiz: PropTypes.bool,
    affiliate_clone_quiz_url: PropTypes.string,
    type: PropTypes.string,
  }),
};

export default function ProductCard({ product, sx, onClick, ...other }) {
  const theme = useTheme();

  return (
    <Card
      sx={{
        backgroundColor: 'background.default',
        color: 'white',
        borderRadius: 2,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        ...sx,
      }}
      {...other}
      onClick={onClick}
    >
      <CardMedia
        component="img"
        image={product.image || `/assets/image-placeholder-${theme.palette.mode}.svg`}
        alt="Course Image"
        sx={{
          objectFit: 'cover',
          width: '100%',
          aspectRatio: '1/1',
        }}
      />
      <CardContent sx={{ padding: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box color="text.primary" sx={{ overflow: 'hidden', maxWidth: '75%' }}>
            <Tooltip title={product.name} placement="top">
              <Typography
                variant="h6"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.2,
                }}
              >
                {product.name}
              </Typography>
            </Tooltip>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography color="success.main" variant="body2">
              {Math.max(product.temperature, 0)}°
            </Typography>
            <img src="/assets/icons/showcase/cakto.png" alt="Cakto" width={18} height={28} />
          </Box>
        </Box>
        <Typography variant="caption" mt={1} color="text.secondary">
          Você recebe até
        </Typography>
        <Typography variant="h4" color="success.main" fontWeight="bold">
          {fCurrency(product.affiliate_commission_amount)}
        </Typography>

        <Divider sx={{ my: 1 }} />

        <ProductTools product={product} />
      </CardContent>
    </Card>
  );
}

ProductCard.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.string,
    image: PropTypes.string,
    name: PropTypes.string,
    author: PropTypes.string,
    affiliate_commission: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    affiliate_share_bump: PropTypes.bool,
    affiliate_share_upsell: PropTypes.bool,
    sales_page: PropTypes.string,
    type: PropTypes.string,
    temperature: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    affiliate_commission_amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  sx: PropTypes.object,
  onClick: PropTypes.func,
};
